import React, { useRef } from "react";

interface OtpInputProps {
  length?: number;
  value: string;
  onChange: (otp: string) => void;
}

const OtpInputComponent: React.FC<OtpInputProps> = ({
  length = 6,
  value,
  onChange,
}) => {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  // Focus next input helper
  const focusInput = (index: number) => {
    inputsRef.current[index]?.focus();
  };

  const handleChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (!/^\d*$/.test(val)) return;

    // Handle paste or multi-char input (e.g. autofill)
    if (val.length > 1) {
      const pasteData = val.slice(0, length);
      onChange(pasteData);

      // Focus appropriate input
      if (pasteData.length < length) {
        focusInput(pasteData.length);
      } else {
        // If full, focus last or leave blur
        inputsRef.current[length - 1]?.focus();
      }
      return;
    }

    // Single char input
    const otpArray = value.split("");
    // Ensure array is length sized with spaces if needed, but simple split is mostly fine given we manage value as string
    // Padding to ensure index is valid if value was shorter
    while (otpArray.length < length) otpArray.push("");

    otpArray[index] = val;
    const newOtp = otpArray.join("").slice(0, length);

    onChange(newOtp);

    // Auto advance
    if (val && index < length - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace") {
      if (!value[index] && index > 0) {
        // Empty and backspace -> move prev and delete
        const otpArray = value.split("");
        otpArray[index - 1] = "";
        onChange(otpArray.join(""));
        focusInput(index - 1);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      focusInput(index - 1);
    } else if (e.key === "ArrowRight" && index < length - 1) {
      focusInput(index + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text/plain").replace(/\D/g, "").slice(0, length);
    if (pasteData) {
      onChange(pasteData);
      const nextIndex = Math.min(pasteData.length, length - 1);
      focusInput(nextIndex);
    }
  };

  return (
    <div className="flex justify-center gap-3">
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={(el) => { inputsRef.current[index] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={index === 0 ? length : 1} // First input allows paste/autofill of full length
          value={value[index] || ""}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          className="h-12 w-12 rounded-lg border border-gray-300 text-center text-lg font-semibold focus:border-fin-primary focus:outline-none focus:ring-2 focus:ring-blue-100 transition-all shadow-sm"
        />
      ))}
    </div>
  );
};

export default OtpInputComponent;
